import React from 'react'
import { useAppSelector } from '@SonarCX/hooks/redux'
import clsx from 'clsx'
import { v4 as uuidv4 } from 'uuid'
import { People } from 'iconsax-react'
import { useTheme } from '@mui/material'

import Tooltip from '../../../../atoms/Tooltip'
import AvatarArea from '../../../../molecules/BaseConversation/AvatarArea'

import { withinTimeInterval, formatDate } from '@SonarCX/timeUtilities'
import { formatTimeStamp } from '@SonarCX/utilities'
import { getTooltipInfo } from './TooltipDetails'
import { selectPresence } from '@SonarCX/slices/presence'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import useUserPermissions from '@SonarCX/hooks/permissions/useUserPermissions'
import useStyles from './styles'

const AvatarSection = ({
  avatarProps,
  names,
  nameClass,
  convo,
  isPersonalSpace = false
}) => {
  const theme = useTheme()

  const { allUsers } = useUserAPI()
  const { userIDs } = avatarProps
  const { presence } = useAppSelector(selectPresence)
  const { isSmall } = useBreakpoint()
  const { hasBasic } = useUserPermissions()

  const classes = useStyles({ smallMedia: isSmall })

  const timeDetails = React.useCallback(
    (time, user) => {
      return (
        <div key={uuidv4()}>
          {user ? (
            <div className={classes.container}>
              <div className={classes.name}>{user}</div>
              <div>
                <span>Last seen:</span>
                <span> {formatTimeStamp(time)}</span>
              </div>
            </div>
          ) : (
            <div className={classes.faded}>
              <span>Last seen:</span>
              <span className={classes.time}>
                {' '}
                {time ?? formatTimeStamp(time)}
              </span>
            </div>
          )}
        </div>
      )
    },
    [classes.container, classes.name, classes.time, classes.faded]
  )

  const getLastOnlineTime = React.useCallback(() => {
    if (!hasBasic) {
      return
    }

    if (userIDs.length === 1) {
      if (!allUsers[userIDs[0]]) {
        return
      }

      // if user is online, return
      if (presence[userIDs[0]]) {
        if (presence[userIDs[0]] === 'online') {
          return <label className={classes.faded}> Online now </label>
        }
        if (presence[userIDs[0]] === 'away') {
          return <label className={classes.faded}> Away </label>
        }
      }

      const time = allUsers[userIDs[0]]?.online_status.last_seen

      if (!time) {
        return <div className={classes.faded}>Last online: Never</div>
      }

      const newDate = withinTimeInterval(time)

      let formatted

      switch (newDate) {
        case 0:
          formatted = formatTimeStamp(time)
          break
        case 1:
          formatted = formatDate(time, 'EEE, h:mm aaa')
          break
        case 2:
          formatted = formatDate(time, 'd MMM, h:mm aaa')
          break
        case 3:
          formatted = formatDate(time, 'd MMM, yyyy')
          break

        default:
          formatted = formatTimeStamp(time)
      }

      return timeDetails(formatted)
    } else {
      // check how many users are online
      let online = []
      let allOnline = false
      userIDs.forEach(id => {
        if (
          presence[id] &&
          (presence[id] === 'online' || presence[id] === 'away')
        ) {
          online.push(id)
        }
      })

      if (online.length) {
        if (online.length === userIDs.length) {
          allOnline = true
        }

        if (allOnline) {
          return <div className={classes.faded}>Everyone is online </div>
        } else {
          if (online.length === 1) {
            return (
              <div className={classes.faded}>
                {allUsers[online[0]]?.name ?? ''} is {presence[online[0]]}
              </div>
            )
          } else if (online.length > 1) {
            return <div className={classes.faded}>Some are online</div>
          }
        }

        return null
      } else {
        return <div className={classes.faded}>Everyone offline</div>
      }
    }
  }, [userIDs, allUsers, presence, timeDetails, classes.faded, hasBasic])

  if (isPersonalSpace) {
    return (
      <div className={classes.root}>
        <div className={classes.avatarArea}>
          <AvatarArea
            userIDs={[convo?.user?.id]}
            isDirect
            intersect={undefined}
          />
          <div>
            <div className={nameClass}>
              <div className={clsx(isSmall && classes.names)}>
                {names} (You)
              </div>

              <div className={classes.type}>Personal space</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (Object.keys(allUsers).length === 0) {
    return <div>Loading...</div>
  }

  return (
    <div className={classes.root}>
      <Tooltip
        title={
          userIDs.length > 1 ? (
            <div className={classes.tooltip}>
              {getTooltipInfo(allUsers, userIDs, presence)}
            </div>
          ) : (
            ''
          )
        }
        enableInteractive
      >
        <div className={classes.avatarArea}>
          <AvatarArea {...avatarProps} />

          <div>
            <div className={nameClass}>
              <div className={clsx(isSmall && classes.names)}>{names}</div>

              {convo.isGroup ? (
                <div className={classes.count}>
                  <div className='number'>{userIDs.length}</div>

                  <People size={theme.size.base} className='icon' />
                </div>
              ) : (
                <div className={classes.type}>Private</div>
              )}
            </div>

            <div className={classes.lastSeen}>{getLastOnlineTime()}</div>
          </div>
        </div>
      </Tooltip>
    </div>
  )
}

export default AvatarSection
